import * as React from 'react';
import Box from '@mui/material/Box';
import { Table } from '@organisms';
import { useState, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TableContext, callDeleteKey, callGetKeys, useBillingAccounts } from '@particles';
import { Button, TextView } from '@atoms';
import generateRows from './partials/generateRows';
import columns from './partials/columns';
import { Snackbar, Stack } from '@mui/material';
import AddKeyModal from './partials/createKeyModal';
import EditKeyModal from './partials/editKeyModal';
import { RouteComponentProps } from '@reach/router';

const APIList: React.FC<RouteComponentProps> = () => {
  const {
    state: {
      apiKeys: { page, perPage, orderBy }
    }
  } = useContext(TableContext);
  const [openAddToken, setOpenAddToken] = useState<boolean>(false);
  const [editingKey, setEditingKey] = useState<Keys | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [error] = useState<ResponseErrorObj>();
  const [copied, setCopied] = useState<boolean>(false);

  const { data: billingResponse } = useBillingAccounts({ type: 'c', status: 'live', caller: 'api-settings' });
  const locationIdToName = useMemo(() => {
    const accounts = billingResponse?.data?.data ?? [];
    return Object.fromEntries(accounts.map((a) => [a.locationId, a.companyName ?? a.locationId]));
  }, [billingResponse]);

  const keysQuery = useQuery({
    queryKey: ['api-keys', page, perPage, orderBy],
    queryFn: () => callGetKeys()
  });

  const keys: Keys[] | undefined = keysQuery.data?.code === 200 ? keysQuery.data.data?.data : undefined;
  const loading = keysQuery.isFetching || deleteLoading;
  const refetch = () => keysQuery.refetch();

  const copyKey = (key: Keys) => {
    if (!key.key) return;
    navigator.clipboard.writeText(key.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const deleteKey = async (key: Keys) => {
    setDeleteLoading(true);
    try {
      const { code } = await callDeleteKey(key.id);
      if (code == 200) {
        await refetch();
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const rowData: TableRow[] = keys
    ? generateRows(keys, (key) => deleteKey(key), copyKey, locationIdToName, (key) => setEditingKey(key))
    : [];
  return (
    <Box p={3}>
      <Box mt={4}>
        <Stack>
          <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextView display={'initial'} $fontSize={22}>
              Manage Your API Keys
            </TextView>
            <Box sx={{ flex: 1 }} />
            <Button
              label="Create API Key"
              onClick={() => {
                setOpenAddToken(true);
              }}
              size="small"
            />
          </Box>
        </Stack>
        <Table name="api-keys" columns={columns} data={rowData} error={error} isLoading={loading} />

        <AddKeyModal
          open={openAddToken}
          onClose={() => {
            setOpenAddToken(false);
            refetch();
          }}
        />

        <EditKeyModal
          open={editingKey !== null}
          onClose={() => {
            setEditingKey(null);
            refetch();
          }}
          apiKey={editingKey}
          locationIdToName={locationIdToName}
        />

        <Snackbar
          open={copied}
          autoHideDuration={2000}
          onClose={() => setCopied(false)}
          message="API key copied to clipboard"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </Box>
  );
};

export default APIList;
